import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, Star, CreditCard, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralLeaderboard = () => {
  const { t } = useLanguage();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/referrals/leaderboard`);
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-400" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-300" />;
      case 3:
        return <Medal className="w-5 h-5 text-amber-600" />;
      default:
        return <Star className="w-5 h-5 text-gray-500" />;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
      case 2:
        return 'bg-gradient-to-r from-gray-400/10 to-gray-300/10 border-gray-400/30';
      case 3:
        return 'bg-gradient-to-r from-amber-600/10 to-amber-500/10 border-amber-600/30';
      default:
        return 'bg-white/5 border-white/10';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-6"
      data-testid="referral-leaderboard"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20">
          <Trophy className="w-6 h-6 text-yellow-400" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">Top Referidores</h3>
          <p className="text-sm text-gray-400">Los mejores embajadores de CloudTexa</p>
        </div>
      </div>

      {/* Leaderboard List */}
      {leaderboard.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Sé el primero en el leaderboard</p>
          <p className="text-sm">¡Invita amigos y aparece aquí!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leaderboard.map((leader, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-center gap-4 p-4 rounded-xl border ${getRankStyle(leader.rank)}`}
              data-testid={`leaderboard-rank-${leader.rank}`}
            >
              {/* Rank */}
              <div className="flex items-center justify-center w-10 h-10">
                {leader.rank <= 3 ? (
                  getRankIcon(leader.rank)
                ) : (
                  <span className="text-lg font-bold text-gray-500">#{leader.rank}</span>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{leader.display_name}</p>
                <p className="text-xs text-gray-500 capitalize">{leader.plan || 'free'} plan</p>
              </div>

              {/* Stats */}
              <div className="text-right">
                <p className="text-lg font-bold text-white">{leader.referral_count}</p>
                <p className="text-xs text-gray-500">referidos</p>
              </div>

              {/* Credits Earned */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10">
                <CreditCard className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-green-400">+{leader.credits_earned}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-white/10 text-center">
        <p className="text-sm text-gray-500">
          Invita amigos con tu código y sube en el ranking
        </p>
      </div>
    </motion.div>
  );
};
